import { notificationManagementService } from '@/services/notificationManagementService/notificationManagementService';
import {
  CreateNotifications,
  NotificationCount,
  NotificationFilters,
  NotificationListResponse,
  Notifications,
} from '@/types/notification.type';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

interface NotificationManagementState {
  list: NotificationListResponse;
  selected: Notifications | null;
  loading: boolean;
  isCreating: boolean;
  isSavingDraft: boolean;
  error: string | null;
  searchFilters: NotificationFilters;
  notificationInfo: NotificationCount;
  createdNotification: any;
  recipientCount: number;
}

const initialState: NotificationManagementState = {
  list: {
    data: [],
    meta: {
      count: 0,
      page: 0,
      limit: 0,
      totalPages: 0,
    },
  },
  selected: null,
  loading: false,
  isCreating: false,
  isSavingDraft: false,
  error: null,
  searchFilters: {
    search: '',
  },
  notificationInfo: {
    data: {
      totalNotification: 0,
      totalScheduledNotification: 0,
      draftNotification: 0,
    },
  },
  createdNotification: null,
  recipientCount: 0,
};

// fetchNotificationList getNotificationsList

export const fetchNotificationList = createAsyncThunk<
  NotificationListResponse,
  NotificationFilters
>('notification/fetchAll', async (filters: NotificationFilters | undefined, thunkAPI) => {
  try {
    const response = await notificationManagementService.getNotificationsList(filters);
    return response;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response?.data?.message || 'Error fetching Notification');
  }
});

export const createNotification = createAsyncThunk(
  'notification/create',
  async (data: CreateNotifications | FormData, thunkAPI) => {
    try {
      return await notificationManagementService.createNotification(data);
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || 'Error creating Notification');
    }
  },
);

export const resendNotification = createAsyncThunk(
  'notification/resend-failed',
  async (id: string, thunkAPI) => {
    try {
      return await notificationManagementService.resendNotification(id);
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || 'Error creating Notification');
    }
  },
);

export const getNotificationById = createAsyncThunk<Notifications, string>(
  'notification/getById',
  async (id: string, thunkAPI) => {
    try {
      return (await notificationManagementService.getNotificationById(id)) as Notifications;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || 'Error getting Notification');
    }
  },
);

export const updateNotification = createAsyncThunk<any, { id: string; formData: FormData }>(
  'notification/update',
  async ({ id, formData }: { id: string; formData: FormData }, thunkAPI) => {
    try {
      const response = await notificationManagementService.updateNotification(id, formData);
      return response;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || 'Error updating Notification');
    }
  },
);

export const deleteNotification = createAsyncThunk(
  'notification/softDelete',
  async ({ id, deletedBy }: { id: string; deletedBy: string }, thunkAPI) => {
    try {
      return await notificationManagementService.deleteNotification(id, deletedBy);
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || 'Error deleting Notification');
    }
  },
);

export const getNotificationsInfo = createAsyncThunk<NotificationCount, void>(
  'notification/getInfo',
  async (_, thunkAPI) => {
    try {
      return await notificationManagementService.getNotificationInfo();
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || 'Error getting Notification');
    }
  },
);

const notificationManagementSlice = createSlice({
  name: 'notification',
  initialState,
  reducers: {
    clearNotificationError(state) {
      state.error = null;
    },
    clearCreatedNotification(state) {
      state.createdNotification = null;
      state.recipientCount = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotificationList.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchNotificationList.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload;
      })
      .addCase(fetchNotificationList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getNotificationsInfo.pending, (state) => {
        state.loading = true;
      })
      .addCase(getNotificationsInfo.fulfilled, (state, action) => {
        state.loading = false;
        state.notificationInfo = action.payload;
      })
      .addCase(getNotificationsInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder.addCase(getNotificationById.fulfilled, (state) => {
      state.loading = false;
    });

    builder.addCase(deleteNotification.fulfilled, (state) => {
      state.loading = false;
    });

    builder
      .addCase(createNotification.pending, (state, action) => {
        const formData = action.meta.arg as FormData;
        if (formData instanceof FormData && formData.get('status') === 'DRAFT') {
          state.isSavingDraft = true;
        } else {
          state.isCreating = true;
        }
      })
      .addCase(createNotification.fulfilled, (state, action) => {
        state.isCreating = false;
        state.isSavingDraft = false;
        state.createdNotification = action.payload?.data?.notification;
        state.recipientCount = action.payload?.data?.recipientCount || 0;
      })
      .addCase(createNotification.rejected, (state) => {
        state.isCreating = false;
        state.isSavingDraft = false;
      });

    builder
      .addCase(updateNotification.pending, (state, action) => {
        const formData = action.meta.arg?.formData as FormData;
        if (formData instanceof FormData && formData.get('status') === 'DRAFT') {
          state.isSavingDraft = true;
        } else {
          state.isCreating = true;
        }
      })
      .addCase(updateNotification.fulfilled, (state, action) => {
        state.isCreating = false;
        state.isSavingDraft = false;
        state.createdNotification = action.payload?.data?.notification;
        state.recipientCount = action.payload?.data?.recipientCount || 0;
      })
      .addCase(updateNotification.rejected, (state) => {
        state.isCreating = false;
        state.isSavingDraft = false;
      });
  },
});

export const { clearNotificationError, clearCreatedNotification } =
  notificationManagementSlice.actions;
export default notificationManagementSlice.reducer;
